import React, {useEffect, useState} from 'react';
import {FiFolder, FiEdit2, FiTrash2, FiCheck, FiX, FiFileText} from 'react-icons/fi';
import {COMBINED_FOLDER_ID, TagRead, JobRead, JobStatus} from "../../schemas";
import JobProgressList from "../../components/Jobs";
import {useNavigationContext} from "../../contexts/NavContext/nav-context";
import {useJobContext} from "../../contexts/job_context";
import {useGroupContext} from "../../contexts/group_context";
import {useFolderContext} from "../../contexts/folder_context";
import {AppView} from "../../App";
import {updateFolder, deleteFolder} from "../../api";
import Swal from "sweetalert2";
import {Brand, Border, Ink, Motion, Radii, Shadows, Surface, ThemeColors} from "../../colors";
import {withApiErrorHandlerThrow} from "../../error_handling";

interface FolderPreviewProps {
    folder: TagRead;  // Folder is now a TagRead with type=FOLDER_UPLOAD
    size?: 'sm' | 'md' | 'lg';
    onRefresh?: () => void;
    /** Pre-fetched file data from batch API call. */
    previewFileNames?: Array<{id: string; name: string}>;
}

const FolderPreview: React.FC<FolderPreviewProps> = ({folder, size = 'sm', onRefresh, previewFileNames}) => {
    const {navigateTo} = useNavigationContext()!;
    const {activeJobs} = useJobContext()!;
    const {currentGroup} = useGroupContext()!;
    const {refreshGroupFolders} = useFolderContext()!;

    const [folderJobs, setFolderJobs] = useState<JobRead[]>([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState(folder.tag_name);
    const [editDescription, setEditDescription] = useState(folder.description || '');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setFolderJobs(
            activeJobs.filter(
                (job) =>
                    job.folder_ids?.includes(folder.id) || job.folder_ids?.includes(COMBINED_FOLDER_ID)
            )
        );
    }, [activeJobs, folder.id]);

    // Filter warnings to only those belonging to this folder
    const folderWarningJobs = folderJobs.map(job => {
        if (!job.warnings?.length) return job;
        const folderWarnings = job.warnings.filter(
            w => w.folder_id === folder.id
        );
        if (folderWarnings.length === job.warnings.length) return job;
        if (folderWarnings.length === 0) return {...job, warnings: undefined};
        return {...job, warnings: folderWarnings};
    });

    const uploadsInProgress = folderWarningJobs.filter(
        (job) => (job.status === JobStatus.PROCESSING || job.status === JobStatus.QUEUED)
            || (job.warnings && job.warnings.length > 0)
    );

    // File preview: use pre-fetched names from batch call, or empty
    const previewFiles = previewFileNames ?? [];
    const PREVIEW_LIMIT = 10;

    const handleEdit = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (isEditing) {
            // Save the edit
            const nameChanged = editName.trim() && editName.trim() !== folder.tag_name;
            const descChanged = editDescription.trim() !== (folder.description || '');

            if (nameChanged || descChanged) {
                setLoading(true);
                try {
                    await withApiErrorHandlerThrow(
                        () => updateFolder(
                            folder.id,
                            editName.trim() || folder.tag_name,
                            editDescription.trim() || undefined
                        ),
                        {titleQualifier: "Error Updating Folder", navigateTo}
                    );
                    await refreshGroupFolders();
                    if (onRefresh) await onRefresh();
                    await Swal.fire({
                        title: "Success",
                        text: "Folder updated",
                        icon: "success",
                        timer: 1500,
                        showConfirmButton: false,
                    });
                } finally {
                    setLoading(false);
                }
            }
            setIsEditing(false);
        } else {
            setEditName(folder.tag_name);
            setEditDescription(folder.description || '');
            setIsEditing(true);
        }
    };

    const handleDelete = async (e: React.MouseEvent) => {
        e.stopPropagation();

        // First warning
        const firstConfirm = await Swal.fire({
            title: "Delete Folder?",
            html: `<p>Are you sure you want to delete <strong>${folder.tag_name}</strong>?</p>
                   <p class="text-red-600 mt-2">This will permanently delete all ${folder.document_count} document(s) in this folder.</p>`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: ThemeColors.neutralDarkHex,
            confirmButtonText: "Yes, delete it",
            cancelButtonText: "Cancel",
        });

        if (!firstConfirm.isConfirmed) return;

        // Second warning - type folder name
        const secondConfirm = await Swal.fire({
            title: "Final Confirmation",
            html: `<p>This action <strong>cannot be undone</strong>.</p>
                   <p class="mt-2">Type <strong>${folder.tag_name}</strong> to confirm deletion:</p>`,
            icon: "error",
            input: "text",
            inputPlaceholder: "Type folder name here",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: ThemeColors.neutralDarkHex,
            confirmButtonText: "Delete Forever",
            cancelButtonText: "Cancel",
            inputValidator: (value) => {
                if (value !== folder.tag_name) {
                    return "Folder name doesn't match. Please type it exactly.";
                }
                return null;
            },
        });

        if (!secondConfirm.isConfirmed) return;

        // Actually delete
        setLoading(true);
        try {
            await withApiErrorHandlerThrow(
                () => deleteFolder(folder.id),
                {titleQualifier: "Error Deleting Folder", navigateTo}
            );
            await refreshGroupFolders();
            if (onRefresh) await onRefresh();
            await Swal.fire({
                title: "Deleted",
                text: "Folder has been permanently deleted.",
                icon: "success",
                timer: 2000,
                showConfirmButton: false,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            handleEdit(e as unknown as React.MouseEvent);
        } else if (e.key === 'Escape') {
            setIsEditing(false);
            setEditName(folder.tag_name);
            setEditDescription(folder.description || '');
        }
    };

    // Width multiplier based on size
    const sizeClass =
        size === 'lg'
            ? 'col-span-3'
            : size === 'md'
                ? 'col-span-2'
                : 'col-span-1'; // sm (default)

    return (
        <div
            onClick={() => {
                if (!isEditing) {
                    navigateTo({
                        view: AppView.DocumentSearch,
                        folder,
                        group: currentGroup!,
                    });
                }
            }}
            className={`group/card relative flex flex-col w-full h-full cursor-pointer
                ${Radii.lg} ${Surface.base} ${Border.DEFAULT} ${Shadows.sm} p-5
                hover:border-[#bcabe0] hover:shadow-[0_8px_24px_-14px_rgba(106,78,170,0.25)]
                ${Motion.base} ${sizeClass}`}
        >
            {/* Action buttons - top right */}
            <div className="absolute top-2.5 right-2.5 flex gap-1 opacity-0 group-hover/card:opacity-100 transition-opacity">
                <button
                    onClick={handleEdit}
                    disabled={loading}
                    className={`p-1.5 ${Radii.md} ${Brand.soft} ${Brand.softHover} ${Motion.fast}`}
                    title={isEditing ? "Save" : "Edit name"}
                >
                    <FiEdit2 className="text-sm"/>
                </button>
                <button
                    onClick={handleDelete}
                    disabled={loading}
                    className={`p-1.5 ${Radii.md} bg-red-50 text-red-600 hover:bg-red-100 ${Motion.fast}`}
                    title="Delete folder"
                >
                    <FiTrash2 className="text-sm"/>
                </button>
            </div>

            {/* Top: Folder info */}
            <div className="flex items-start gap-3">
                <div className={`p-2.5 ${Radii.md} ${Brand.soft} flex-shrink-0`}>
                    <FiFolder className="text-xl"/>
                </div>
                <div className="flex flex-col min-w-0 flex-1 gap-1">
                    {isEditing ? (
                        <>
                            <input
                                type="text"
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                onKeyDown={handleKeyDown}
                                onClick={(e) => e.stopPropagation()}
                                className={`font-medium ${Ink.primary} ${Border.DEFAULT} ${Radii.md} px-2 py-1.5 text-sm w-full ${Brand.ring} ${Motion.fast}`}
                                placeholder="Folder name"
                                autoFocus
                            />
                            <input
                                type="text"
                                value={editDescription}
                                onChange={(e) => setEditDescription(e.target.value)}
                                onKeyDown={handleKeyDown}
                                onClick={(e) => e.stopPropagation()}
                                className={`text-xs ${Ink.secondary} ${Border.DEFAULT} ${Radii.md} px-2 py-1.5 w-full ${Brand.ring} ${Motion.fast}`}
                                placeholder="Description (optional)"
                            />
                            <div className="flex gap-1.5 mt-1">
                                <button
                                    onClick={handleEdit}
                                    disabled={loading}
                                    className={`flex items-center gap-1 px-2.5 py-1 text-xs font-semibold ${Brand.solid} ${Brand.solidHover} ${Radii.md} ${Motion.fast} disabled:opacity-50`}
                                >
                                    <FiCheck className="text-xs"/>
                                    Save
                                </button>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        setIsEditing(false);
                                        setEditName(folder.tag_name);
                                        setEditDescription(folder.description || '');
                                    }}
                                    className={`flex items-center gap-1 px-2.5 py-1 text-xs font-medium ${Ink.secondary} ${Surface.sunken} hover:bg-[#e8e8ec] ${Radii.md} ${Motion.fast}`}
                                >
                                    <FiX className="text-xs"/>
                                    Cancel
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <span className={`font-semibold ${Ink.primary} break-words whitespace-normal`}>
                                {folder.tag_name}
                            </span>
                            <span className={`text-xs ${Ink.muted} break-words whitespace-normal`}>
                                {folder.description || "No description"}
                            </span>
                        </>
                    )}
                </div>
            </div>

            {/* Middle: Upload progress */}
            {uploadsInProgress.length > 0 && (() => {
                const failedJobs = uploadsInProgress.filter(j => j.status === JobStatus.FAILED);
                const activeJobs_ = uploadsInProgress.filter(j => j.status !== JobStatus.FAILED);
                const hasFailed = failedJobs.length > 0;
                const hasActive = activeJobs_.length > 0;

                return (
                    <div className={`mt-3 ${hasFailed && !hasActive ? 'rounded-lg border border-red-200 bg-red-50/50 p-2' : ''}`}>
                        {hasActive && (
                            <div className="flex items-center gap-2 mb-1">
                                <div className="h-1.5 w-1.5 rounded-full bg-[#7e63bf] animate-pulse"/>
                                <span className={`text-xs ${Ink.muted}`}>Processing</span>
                            </div>
                        )}
                        {hasActive && <JobProgressList jobs={activeJobs_} compact/>}
                        {hasFailed && (
                            <>
                                {hasActive && <div className="mt-2 border-t border-red-200 pt-2"/>}
                                <JobProgressList jobs={failedJobs} compact/>
                            </>
                        )}
                    </div>
                );
            })()}

            {/* File preview list */}
            {/* File preview list (skip for All Documents) */}
            {folder.id !== COMBINED_FOLDER_ID && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex-1 min-h-[6rem]">
                    {previewFiles.length > 0 ? (
                        <div className="space-y-1 max-h-[7.5rem] overflow-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-200 [&::-webkit-scrollbar-track]:bg-transparent" style={{scrollbarWidth: 'thin'}}>
                            {previewFiles.map((file, i) => (
                                <a
                                    key={file.id || i}
                                    href={`/app/workspace/${currentGroup!.id}/${folder.id}/${file.id}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="flex items-center gap-2 text-[11px] text-slate-500 group/file cursor-pointer hover:bg-slate-50 rounded px-1 -mx-1 no-underline"
                                >
                                    <FiFileText className="w-3 h-3 text-slate-300 shrink-0 group-hover/file:text-indigo-400"/>
                                    <span className="truncate group-hover/file:text-slate-700">{file.name}</span>
                                </a>
                            ))}
                            {(folder.document_count ?? 0) > PREVIEW_LIMIT && (
                                <div className="text-[10px] text-slate-400 pl-5 italic">
                                    +{(folder.document_count ?? 0) - PREVIEW_LIMIT} more
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="flex items-center justify-center h-full text-[11px] text-slate-300 italic">
                            Empty folder
                        </div>
                    )}
                </div>
            )}

            {/* Bottom: Document count + date */}
            <div className="mt-3 pt-2 border-t border-slate-100 flex items-center justify-between">
                <span className="text-[10px] text-slate-400">
                    {folder.created_at ? new Date(folder.created_at).toLocaleDateString(undefined, {month: 'short', day: 'numeric', year: 'numeric'}) : ''}
                </span>
                <span className={`text-xs font-medium ${Ink.muted}`}>
                    {folder.document_count ?? 0} doc{folder.document_count !== 1 ? 's' : ''}
                </span>
            </div>
        </div>
    );
};


export {FolderPreview};
